import { RequestOptions } from "http";
import { GrailedError } from "./GrailedError";

type Response<T> = {
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  readonly json: () => Promise<T>;
  readonly headers?: { readonly get: (name: string) => string | null };
};

type ErrorResponse = Pick<
  Response<unknown>,
  "status" | "statusText" | "url"
> & {
  readonly headers: { readonly get: (name: string) => string | null };
};

/**
 * GrailedAPIError is thrown when a request to the Grailed API fails.
 *
 * Next.js prints uncaught errors with every enumerable field, and that dump reaches Datadog, so
 * the error deliberately does not retain the request options (whose headers carry the forwarded
 * cookie and auth token on SSR requests) or the raw Response (whose headers carry set-cookie).
 * Response headers stay readable through `response.headers.get` for Sentry's source tagging.
 */
export default class GrailedAPIError extends Error {
  response: ErrorResponse;

  body: {
    error?: GrailedError;
  };

  cause?: unknown;

  constructor(
    response: Response<unknown>,
    body: {
      error?: GrailedError;
    },
    _options: RequestOptions | RequestInit | null | undefined,
    cause?: unknown,
  ) {
    const { status, statusText, url, headers } = response;
    const message = `${status} ${statusText} for ${url}`;
    super(message);
    this.name = "GrailedAPIError";
    this.response = {
      status,
      statusText,
      url,
      headers: { get: (name) => headers?.get(name) ?? null },
    };
    this.body = body;
    this.cause = cause;
  }
}

const fromJSON =
  (response: Response<unknown>, options: RequestOptions | RequestInit) =>
  (json: Record<string, any>): GrailedAPIError =>
    new GrailedAPIError(response, json, options);

export const fromReason =
  (response: Response<unknown>, options: RequestOptions | RequestInit) =>
  (reason: unknown): GrailedAPIError =>
    new GrailedAPIError(response, {}, options, reason);

export const fromResponse =
  (options: RequestOptions | RequestInit) =>
  (response: Response<unknown>): Promise<GrailedAPIError> =>
    response
      .json()
      // @ts-expect-error strictFunctionTypes - enabling strict mode
      .then(fromJSON(response, options))
      .catch(fromReason(response, options));
